import React, { useState } from 'react';
import { Formik, Field, Form, FieldProps } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router';
import { DarkButton, PrimaryButtonLarge } from '../../../../_components/buttons';
import { useAuth } from '../../../modules/auth/AuthContext';
import authService from "../../../../services/auth.service";

interface ResetPasswordFormValues {
    email?: string;
    password?: string;
    confirmPassword?: string;
}

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const authContext = useAuth();
    const [loading, setLoading] = useState(false);

    // Extract "token" query parameter
    const token = new URLSearchParams(location.search).get('token');

    // Initial form values depend on whether we have a token
    const initialValues: ResetPasswordFormValues = token
        ? { password: '', confirmPassword: '' }
        : { email: '' };

    // Validation schema also switches based on token presence
    const validationSchema = token
        ? Yup.object({
            password: Yup.string()
                .min(6, 'Password must be at least 6 characters')
                .required('Password is required'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password')], 'Passwords must match')
                .required('Confirm Password is required'),
        })
        : Yup.object({
            email: Yup.string()
                .email('Invalid email address')
                .required('Email is required'),
        });

    const handleSubmit = async (
        values: ResetPasswordFormValues,
        { setStatus, setSubmitting }: any
    ) => {
        setLoading(true);
        setStatus(undefined);

        try {
            if (token) {
                const response = await authService.resetPassword({
                    token,
                    password: values.password!,
                });
                navigate('/auth/login', { replace: true });
            } else {
                await authService.requestPasswordReset({
                    email: values.email!,
                });
                setStatus(
                    'Success! If an account with that email exists, you will receive a password reset link shortly.'
                );
            }
        } catch (error: any) {
            if(error?.response?.data?.error) {
                setStatus(error?.response?.data?.error);
                return;
            }
            const message =
                error?.response?.data?.message ||
                'Service currently unavailable. Please try again later.';
            setStatus(message);
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    return (
        <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="card p-4 shadow-lg bg-glass card-sm">
                <h1 className="text-center mb-4">
                    {token ? 'Reset Password' : 'Forgot Password?'}
                </h1>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, status }) => (
                        <Form>
                            {status && (
                                <div className={`mb-lg-15 alert ${status.startsWith("Success!") ? 'alert-success' : 'alert-danger'}`}>
                                    <div className="alert-text font-weight-bold">{status}</div>
                                </div>
                            )}

                            {!token && (
                                <div className="form-group mb-3">
                                    <label htmlFor="email" className="form-label">
                                    Email
                                    </label>
                                    <Field name="email">
                                        {({ field, meta }: FieldProps) => (
                                            <div>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    {...field}
                                                    className={`form-control ${
                                                        meta.touched && meta.error ? 'is-invalid' : ''
                                                    }`}
                                                />
                                                {meta.touched && meta.error && (
                                                    <div className="text-danger mt-1">
                                                        {meta.error}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Field>
                                </div>
                            )}

                            {token && (
                                <>
                                    <div className="form-group mb-3">
                                        <label htmlFor="password" className="form-label">
                                            New Password
                                        </label>
                                        <Field name="password">
                                            {({ field, meta }: FieldProps) => (
                                                <div>
                                                    <input
                                                        type="password"
                                                        id="password"
                                                        {...field}
                                                        className={`form-control ${
                                                            meta.touched && meta.error ? 'is-invalid' : ''
                                                        }`}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <div className="text-danger mt-1">
                                                            {meta.error}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label
                                            htmlFor="confirmPassword"
                                            className="form-label"
                                        >
                                            Confirm New Password
                                        </label>
                                        <Field name="confirmPassword">
                                            {({ field, meta }: FieldProps) => (
                                                <div>
                                                    <input
                                                        type="password"
                                                        id="confirmPassword"
                                                        {...field}
                                                        className={`form-control ${
                                                            meta.touched && meta.error ? 'is-invalid' : ''
                                                        }`}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <div className="text-danger mt-1">
                                                            {meta.error}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </div>
                                </>
                            )}

                            <div className="d-grid gap-2">
                                <PrimaryButtonLarge
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                >
                                    {isSubmitting || loading
                                        ? token
                                            ? 'Resetting Password...'
                                            : 'Sending...'
                                        : token
                                            ? 'Reset Password'
                                            : 'Send Reset Link'}
                                </PrimaryButtonLarge>

                                <div className="or-divider">
                                    <span className="line"></span>
                                    <p className="text-secondary fw-bolder text-center mb-0 or-text">
                                        OR
                                    </p>
                                    <span className="line"></span>
                                </div>

                                <DarkButton
                                    disabled={isSubmitting || loading}
                                    onClick={() => navigate('/auth/login')}
                                >
                                    Back to Login
                                </DarkButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ResetPassword;
